// src/pages/finance/PaymentMethods.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  CircularProgress,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  AccountBalance as AccountBalanceIcon,
  Paid as CashIcon,
  Settings as SettingsIcon,
  PhotoCamera as CameraIcon,
  CloudUpload as UploadIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';

// Función para redimensionar y comprimir la imagen en el cliente
const compressAndResizeImage = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        
        // Dimensiones máximas premium para el logo / QR
        const MAX_WIDTH = 400;
        const MAX_HEIGHT = 400;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        canvas.toBlob((blob) => {
          if (blob) {
            const resizedFile = new File([blob], file.name, {
              type: mimeType,
              lastModified: Date.now()
            });
            resolve(resizedFile);
          } else {
            reject(new Error('Error al exportar imagen redimensionada'));
          }
        }, mimeType, 0.85);
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

const PaymentMethodForm = ({ open, onClose, onSubmit, method = null, financialAccounts = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    visible_to_client: true,
    instructions: '',
    logo_url: null,
    qr_url: null,
    financial_account_id: '',
    ...method
  });

  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingQr, setUploadingQr] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData({
        name: '',
        description: '',
        visible_to_client: true,
        instructions: '',
        logo_url: null,
        qr_url: null,
        financial_account_id: '',
        ...method
      });
    }
  }, [open, method]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'visible_to_client' ? checked : value
    }));
  };

  const handleLogoUpload = async (file) => {
    if (!method || !method.id) return;
    setUploadingLogo(true);
    try {
      const optimizedFile = await compressAndResizeImage(file);
      const fd = new FormData();
      fd.append('logo', optimizedFile);
      
      const response = await fetch(`/api/payment-methods/${method.id}/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: fd
      });
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, logo_url: data.logo_url }));
      } else {
        alert('Error al subir el logo');
      }
    } catch (err) {
      console.error(err);
      alert('Error al procesar el logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleQrUpload = async (file) => {
    if (!method || !method.id) return;
    setUploadingQr(true);
    try {
      const optimizedFile = await compressAndResizeImage(file);
      const fd = new FormData();
      fd.append('qr', optimizedFile);
      
      const response = await fetch(`/api/payment-methods/${method.id}/qr`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: fd
      });
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, qr_url: data.qr_url }));
      } else {
        alert('Error al subir el código QR');
      }
    } catch (err) {
      console.error(err);
      alert('Error al procesar el código QR');
    } finally {
      setUploadingQr(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: '24px', p: 1 }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 800, pb: 1, color: '#0f172a' }}>
          {method ? 'Editar Forma de Pago' : 'Nueva Forma de Pago'}
        </DialogTitle>
        <DialogContent>
          <Box className="space-y-4 py-4" sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              fullWidth
              label="Nombre de la Forma de Pago"
              name="name"
              placeholder="Ej. Transferencia BCP, Yape, Efectivo..."
              value={formData.name}
              onChange={handleChange}
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />

            <TextField
              fullWidth
              label="Descripción Corta"
              name="description"
              placeholder="Detalle o uso interno del método..."
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />

            <TextField
              select
              fullWidth
              label="Cuenta Financiera Vinculada"
              name="financial_account_id"
              value={formData.financial_account_id || ''}
              onChange={handleChange}
              helperText="Al vincular una cuenta, los pagos registrados con este método aumentarán su saldo automáticamente."
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            >
              <MenuItem value="">
                <em>Ninguna (No vinculada)</em>
              </MenuItem>
              {financialAccounts.map((acc) => (
                <MenuItem key={acc.id} value={acc.id}>
                  {acc.name} ({acc.type === 'cash' ? 'Caja Chica/Efectivo' : 'Banco'})
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="Instrucciones del Cliente (Datos de Cuenta)"
              name="instructions"
              placeholder="Indique número de cuenta, CCI, titular, y correo de confirmación..."
              value={formData.instructions}
              onChange={handleChange}
              multiline
              rows={4}
              helperText="Estas instrucciones se mostrarán al cliente en su portal al momento de realizar su pago."
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />

            <Box sx={{
              p: 2,
              borderRadius: '12px',
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>
                  Habilitar para el Cliente
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Permitir que el cliente visualice y reporte pagos con este método
                </Typography>
              </Box>
              <Switch
                checked={formData.visible_to_client}
                onChange={handleChange}
                name="visible_to_client"
                color="primary"
              />
            </Box>

            {method && method.id && (
              <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b', mb: 2 }}>
                  Personalización de Imágenes 📸
                </Typography>
                <Grid container spacing={2}>
                  {/* Subida de Logotipo */}
                  <Grid item xs={12} sm={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: '16px', textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyBetween: 'space-between', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', display: 'block', mb: 1.5 }}>
                        Logotipo / Ícono
                      </Typography>
                      
                      <Box sx={{
                        height: '100px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px dashed #cbd5e1',
                        borderRadius: '12px',
                        mb: 2,
                        bgcolor: 'white',
                        overflow: 'hidden',
                        p: 1
                      }}>
                        {formData.logo_url ? (
                          <img src={formData.logo_url} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        ) : (
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>Sin logotipo</Typography>
                        )}
                      </Box>

                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="method-form-logo-input"
                        type="file"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) handleLogoUpload(file);
                        }}
                      />
                      <label htmlFor="method-form-logo-input">
                        <Button
                          variant="outlined"
                          component="span"
                          size="small"
                          fullWidth
                          disabled={uploadingLogo}
                          startIcon={uploadingLogo ? <CircularProgress size={16} /> : <CameraIcon />}
                          sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
                        >
                          {uploadingLogo ? 'Subiendo...' : 'Subir Logo'}
                        </Button>
                      </label>
                    </Paper>
                  </Grid>

                  {/* Subida de Código QR */}
                  <Grid item xs={12} sm={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: '16px', textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyBetween: 'space-between', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', display: 'block', mb: 1.5 }}>
                        Código QR de Pago
                      </Typography>
                      
                      <Box sx={{
                        height: '100px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px dashed #cbd5e1',
                        borderRadius: '12px',
                        mb: 2,
                        bgcolor: 'white',
                        overflow: 'hidden',
                        p: 1
                      }}>
                        {formData.qr_url ? (
                          <img src={formData.qr_url} alt="QR" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        ) : (
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>Sin código QR</Typography>
                        )}
                      </Box>

                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="method-form-qr-input"
                        type="file"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) handleQrUpload(file);
                        }}
                      />
                      <label htmlFor="method-form-qr-input">
                        <Button
                          variant="outlined"
                          component="span"
                          size="small"
                          fullWidth
                          disabled={uploadingQr}
                          startIcon={uploadingQr ? <CircularProgress size={16} /> : <CameraIcon />}
                          sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
                        >
                          {uploadingQr ? 'Subiendo...' : 'Subir QR'}
                        </Button>
                      </label>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button 
            onClick={onClose} 
            sx={{ borderRadius: '12px', textTransform: 'none', color: '#64748b', fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            sx={{ 
              borderRadius: '12px', 
              textTransform: 'none', 
              px: 4, 
              fontWeight: 700,
              backgroundColor: '#6366f1',
              '&:hover': { backgroundColor: '#4f46e5' }
            }}
          >
            Guardar Cambios
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const PaymentMethods = () => {
  const [methods, setMethods] = useState([]);
  const [financialAccounts, setFinancialAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [methodToDelete, setMethodToDelete] = useState(null);

  useEffect(() => {
    fetchMethods();
    fetchAccounts();
  }, []);

  const fetchMethods = async () => {
    try {
      const response = await fetch('/api/payment-methods', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setMethods(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await fetch('/api/accounting/accounts', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setFinancialAccounts(Array.isArray(data) ? data.filter(acc => acc.status === 'active') : []);
    } catch (error) {
      console.error('Error fetching financial accounts:', error);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      const method = selectedMethod ? 'PUT' : 'POST';
      const url = selectedMethod 
        ? `/api/payment-methods/${selectedMethod.id}`
        : '/api/payment-methods';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Error al guardar forma de pago');

      fetchMethods();
      setOpenForm(false);
      setSelectedMethod(null);
    } catch (error) {
      console.error('Error saving payment method:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`/api/payment-methods/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Error al eliminar forma de pago');

      fetchMethods();
      setDeleteConfirmOpen(false);
      setMethodToDelete(null);
    } catch (error) {
      console.error('Error deleting payment method:', error);
    }
  };

  const columns = [
    { 
      field: 'name', 
      headerName: 'Nombre del Método', 
      width: 260,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {params.row.logo_url ? (
            <Avatar
              src={params.row.logo_url}
              variant="rounded"
              sx={{ width: 32, height: 32, border: '1px solid rgba(0,0,0,0.06)', bgcolor: 'transparent' }}
            />
          ) : (
            <Box sx={{
              p: 1,
              borderRadius: '10px',
              backgroundColor: params.value.toLowerCase().includes('efectivo') ? 'rgba(16, 185, 129, 0.1)' : 'rgba(99, 102, 241, 0.1)',
              color: params.value.toLowerCase().includes('efectivo') ? '#10b981' : '#6366f1',
              display: 'flex',
              alignItems: 'center'
            }}>
              {params.value.toLowerCase().includes('efectivo') ? <CashIcon fontSize="small" /> : <AccountBalanceIcon fontSize="small" />}
            </Box>
          )}
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
            {params.value}
          </Typography>
          {params.row.qr_url && (
            <Chip 
              label="QR" 
              size="small" 
              sx={{ height: 18, fontSize: '0.62rem', fontWeight: 'bold', bgcolor: 'rgba(99, 102, 241, 0.12)', color: '#6366f1', px: 0.5, border: 'none' }} 
            />
          )}
        </Box>
      )
    },
    { 
      field: 'description', 
      headerName: 'Descripción', 
      width: 320,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'visible_to_client',
      headerName: 'Visibilidad Portal',
      width: 180,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Visible' : 'Privado'}
          color={params.value ? 'success' : 'default'}
          size="small"
          icon={params.value ? <CheckIcon /> : <CloseIcon />}
          sx={{
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '0.75rem',
            backgroundColor: params.value ? 'rgba(16, 185, 129, 0.1)' : 'rgba(148, 163, 184, 0.1)',
            color: params.value ? '#10b981' : '#64748b',
            border: 'none',
            '& .MuiChip-icon': { color: 'inherit' }
          }}
        />
      )
    },
    {
      field: 'financial_account_name',
      headerName: 'Cuenta Contable',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {params.value ? (
            <>
              <AccountBalanceIcon fontSize="inherit" sx={{ color: '#6366f1' }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>
                {params.value}
              </Typography>
            </>
          ) : (
            <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
              No vinculada
            </Typography>
          )}
        </Box>
      )
    },
    {
      field: 'payment_count',
      headerName: 'Pagos Registrados',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#4f46e5' }}>
          {params.value || 0} trans.
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 130,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedMethod(params.row);
                setOpenForm(true);
              }}
              sx={{ color: '#6366f1', backgroundColor: 'rgba(99, 102, 241, 0.08)', '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.15)' } }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {Number(params.row.payment_count) === 0 && (
            <Tooltip title="Eliminar">
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  setMethodToDelete(params.row);
                  setDeleteConfirmOpen(true);
                }}
                sx={{ backgroundColor: 'rgba(239, 68, 68, 0.08)', '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.15)' } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 2, gap: 1.5 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            Formas de Pago
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b', mt: 0.2, fontWeight: 500, display: 'block' }}>
            Administre las cuentas bancarias, pasarelas de pago y formas de cobro disponibles en el sistema.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedMethod(null);
            setOpenForm(true);
          }}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            px: 2,
            height: '36px',
            fontSize: '0.875rem',
            fontWeight: 700,
            backgroundColor: '#6366f1',
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#4f46e5' },
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          Nueva Forma de Pago
        </Button>
      </Box>

      <Paper
        sx={{
          borderRadius: '20px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
          border: '1px solid rgba(0,0,0,0.05)',
          overflow: 'hidden',
          backgroundColor: 'white'
        }}
      >
        <DataGrid
          rows={methods}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          loading={loading}
          autoHeight
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8fafc',
              borderBottom: '1px solid rgba(0,0,0,0.06)'
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(0,0,0,0.04)'
            }
          }}
        />
      </Paper>

      <PaymentMethodForm
        open={openForm}
        onClose={() => {
          setOpenForm(false);
          setSelectedMethod(null);
        }}
        onSubmit={handleSubmit}
        method={selectedMethod}
        financialAccounts={financialAccounts}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{
          sx: { borderRadius: '20px', p: 1 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1, color: '#0f172a' }}>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#475569', fontWeight: 500 }}>
            ¿Estás seguro de que deseas eliminar la forma de pago "{methodToDelete?.name}"? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{ borderRadius: '12px', textTransform: 'none', color: '#64748b', fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={() => handleDelete(methodToDelete.id)}
            color="error"
            variant="contained"
            sx={{ borderRadius: '12px', textTransform: 'none', px: 3, fontWeight: 700 }}
          >
            Eliminar Forma de Pago
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentMethods;